import Service, { inject as service } from '@ember/service';
import { set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { or } from '@ember/object/computed';
import { AddressResult } from '@square/dashboard-protos/models/squareup/address/service';
import { Verification } from '@square/dashboard-protos/models/squareup/common/address';
import { diff_match_patch } from 'diff-match-patch';

const diffMatchPatch = new diff_match_patch();

export default class AddressValidation extends Service {
  @service sfsAddressApi;
  @service currentApplicationFlow;
  @service router;

  @tracked individualStandardizedAddress = null;
  @tracked individualStandardizationChanges = null;
  @tracked businessStandardizedAddress = null;
  @tracked businessStandardizationChanges = null;

  // This field will be set to true if the address validation service
  // returns that the entire address is invalid.
  @tracked individualFailedFullAddressValidation = false;

  // This field will be set to true if the address validation service
  // returns that the line2 field is invalid.
  @tracked individualFailedLine2Validation = false;

  // This field will be set to true if the address validation service
  // returns that the entire address is invalid.
  @tracked businessFailedFullAddressValidation = false;

  // This field will be set to true if the address validation service
  // returns that the line2 field is invalid.
  @tracked businessFailedLine2Validation = false;

  @tracked didShowIndividualValidation = false;
  @tracked didShowBusinessValidation = false;

  @or(
    'individualFailedFullAddressValidation',
    'individualFailedLine2Validation'
  )
  individualFailedValidation;

  @or('businessFailedFullAddressValidation', 'businessFailedLine2Validation')
  businessFailedValidation;

  getStandardizationChanges(originalAddress, standardizedAddress) {
    /**
     * The Address Service is inconsistent about whether address line 2 is returned
     * as a separate line or whether it is combined with address line 1
     *
     * ex:
     *   addressLine1: 375 Broadway
     *   addressLine2: Suite 300
     * becomes:
     *   addressLine1: 375 Broadway Rm 300
     * after validation and standardization.
     *
     * Setting standardization changes to always compare address line 1 & 2 combined
     * removes changes that are not actually material
     */
    const addressLines = originalAddress.addressLine2
      ? `${originalAddress.addressLine1} ${originalAddress.addressLine2}`
      : originalAddress.addressLine1;
    const responseLines = standardizedAddress.addressLine2
      ? `${standardizedAddress.addressLine1} ${standardizedAddress.addressLine2}`
      : standardizedAddress.addressLine1;

    const standardizationChanges = {
      addressLines: diffMatchPatch.diff_main(addressLines, responseLines),
      locality: diffMatchPatch.diff_main(
        originalAddress.locality,
        standardizedAddress.locality
      ),
      administrativeDistrictLevel1: diffMatchPatch.diff_main(
        originalAddress.administrativeDistrictLevel1,
        standardizedAddress.administrativeDistrictLevel1
      ),
      postalCode: diffMatchPatch.diff_main(
        originalAddress.postalCode,
        standardizedAddress.postalCode
      ),
    };

    return standardizationChanges;
  }

  hasSignificantChanges(standardizationChanges) {
    /**
     * Used to determine if the address should show in the confirmation modal
     *
     * No standardization changes indicates the address failed validation or was unable
     * to be validated. Presence of standardization changes indicates that the address
     * was successfully validated
     *
     * Returns whether the address failed validation, was unable to be validated,
     * or has any significant standardization changes (changes aside from addition of ZIP+4)
     */
    const ZIP_PLUS_FOUR_REGEX = /^-\d{4}$/;

    // Determines whether the address is identical or if only change is addition of ZIP+4
    const noSignificantChanges =
      standardizationChanges &&
      Object.entries(standardizationChanges).every(([field, changes]) => {
        /**
         * No changes for addressLine2 if it isn't present may be presented as an empty array.
         *
         * For other fields and when addressLine2 is present, no changes will be represented as
         * [[0, '375 W Broadway']]
         */
        const hasNoChanges =
          changes.length === 0 || (changes.length === 1 && changes[0][0] === 0);

        if (field === 'postalCode') {
          return (
            hasNoChanges ||
            // Addition of ZIP+4 will be represented as [[0, '10012'], [1, '-4324']]
            (changes.length === 2 &&
              changes[0][0] === 0 &&
              changes[1][0] === 1 &&
              changes[1][1].match(ZIP_PLUS_FOUR_REGEX))
          );
        }

        return hasNoChanges;
      });

    return !noSignificantChanges;
  }

  async validateAndStandardizeIndividual(originalAddress) {
    set(this, 'individualFailedLine2Validation', false);
    set(this, 'individualFailedFullAddressValidation', false);
    set(this, 'individualStandardizedAddress', null);
    set(this, 'individualStandardizedizationChanges', null);

    const response = await this.sfsAddressApi.validateAndStandardizeAddress(
      originalAddress
    );

    if (response.validationStatus === AddressResult.Status.SUCCESS) {
      const standardizedAddress = response.address;
      set(this, 'individualStandardizedAddress', standardizedAddress);

      const standardizedChanges = this.getStandardizationChanges(
        originalAddress,
        standardizedAddress
      );

      set(this, 'individualStandardizationChanges', standardizedChanges);
    } else if (
      response.validationStatus === AddressResult.Status.FAILED_VERIFICATION
    ) {
      set(this, 'individualStandardizationChanges', null);

      if (
        response.validationLevel === Verification.VerificationLevel.PREMISES
      ) {
        set(this, 'individualFailedLine2Validation', true);
      } else {
        set(this, 'individualFailedFullAddressValidation', true);
      }
    }
  }

  async validateAndStandardizeBusiness(originalAddress) {
    set(this, 'businessFailedLine2Validation', false);
    set(this, 'businessFailedFullAddressValidation', false);
    set(this, 'businessStandardizedAddress', null);
    set(this, 'businessStandardizedizationChanges', null);

    const response = await this.sfsAddressApi.validateAndStandardizeAddress(
      originalAddress
    );

    const standardizedAddress = response.address;
    set(this, 'businessStandardizedAddress', standardizedAddress);

    if (response.validationStatus === AddressResult.Status.SUCCESS) {
      const standardizedChanges = this.getStandardizationChanges(
        originalAddress,
        standardizedAddress
      );

      set(this, 'businessStandardizationChanges', standardizedChanges);
    } else if (
      response.validationStatus === AddressResult.Status.FAILED_VERIFICATION
    ) {
      set(this, 'businessStandardizationChanges', null);

      if (
        response.validationLevel === Verification.VerificationLevel.PREMISES
      ) {
        set(this, 'businessFailedLine2Validation', true);
      } else {
        set(this, 'businessFailedFullAddressValidation', true);
      }
    }
  }
}
